import {
  EnvironmentId,
  type PullRequestDiffInput,
  type PullRequestDiffResult,
  type PullRequestDiffFileContentsInput,
  type PullRequestListCursors,
  type PullRequestListInput,
  type PullRequestListResult,
  type PullRequestRef,
  type PullRequestDetail,
  type PullRequestActivity,
  type PullRequestListProjectError,
  type PullRequestListStatsResult,
  type PullRequestListFilters,
  type CmmdPullRequestRef,
  type CmmdPullRequestListInput,
  ProjectId,
} from "@cmmd-forge/contracts";
import { createPullRequestState } from "@cmmd-forge/client-runtime";
import {
  infiniteQueryOptions,
  mutationOptions,
  queryOptions,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";

import { cmmdApiClient } from "../environments/cmmdApi";
import { pullRequestQueryKeys } from "./pullRequestQueryKeys";

const LIST_STALE_TIME_MS = 30_000;
const STATS_STALE_TIME_MS = 60_000;
const DETAIL_STALE_TIME_MS = 15_000;
const DEFERRED_STALE_TIME_MS = 60_000;
const DEFAULT_LIST_PAGE_SIZE = 99;
const MAX_LIST_PAGE_SIZE = 500;
const requestState = createPullRequestState();
export const COMMAND_AUTHORITY_SCOPE = EnvironmentId.make("command-authority");

interface PullRequestListPageParam {
  readonly limit: number;
  readonly cursors?: PullRequestListCursors;
}

interface PullRequestDiffPageParam {
  readonly cursor: string | null;
}

function sharedRead<Result>(key: readonly unknown[], load: () => Promise<Result>): Promise<Result> {
  return requestState.read(JSON.stringify(key), load);
}

export const pullRequestMutationKeys = {
  command: () => ["pullRequests", COMMAND_AUTHORITY_SCOPE, "command"] as const,
};

export function commandPullRequestRef(reference: PullRequestRef): CmmdPullRequestRef {
  if (reference.projectRepositoryId === undefined) {
    throw new Error("This pull request has no canonical Command repository identity.");
  }
  return {
    projectId: commandProjectId(reference.projectId),
    projectRepositoryId: reference.projectRepositoryId,
    number: reference.number,
  };
}

function commandProjectId(projectId: ProjectId): number {
  const numericProjectId = Number(projectId);
  if (!Number.isSafeInteger(numericProjectId) || numericProjectId <= 0) {
    throw new Error(`Forge project ID ${projectId} cannot be used by Command.`);
  }
  return numericProjectId;
}

function forgeProjectId(projectId: number): ProjectId {
  return ProjectId.make(String(projectId));
}

function normalizeListFilters(
  filters: PullRequestListFilters,
): NonNullable<CmmdPullRequestListInput["filters"]> {
  return {
    ...(filters.draft === undefined ? {} : { draft: filters.draft }),
    ...(filters.review === undefined ? {} : { review: filters.review }),
    ...(filters.checks === undefined ? {} : { checks: filters.checks }),
    ...(filters.labels === undefined ? {} : { labels: filters.labels.map((group) => [...group]) }),
    ...(filters.excludedLabels === undefined
      ? {}
      : { excludedLabels: [...filters.excludedLabels] }),
    ...(filters.author === undefined ? {} : { author: filters.author }),
  };
}

function commandListInput(input: PullRequestListInput): CmmdPullRequestListInput {
  const { projectId, projectIds, filters, cursors } = input;
  return {
    state: input.state,
    ...(input.involvement === undefined ? {} : { involvement: input.involvement }),
    ...(input.host === undefined ? {} : { host: input.host }),
    ...(input.query === undefined ? {} : { query: input.query }),
    ...(input.limit === undefined ? {} : { limit: input.limit }),
    ...(projectId === undefined ? {} : { projectId: commandProjectId(projectId) }),
    ...(projectIds === undefined ? {} : { projectIds: projectIds.map(commandProjectId) }),
    ...(filters === undefined ? {} : { filters: normalizeListFilters(filters) }),
    ...(cursors === undefined ? {} : { cursors: { ...cursors } }),
  };
}

function normalizeListResult(
  result: Awaited<ReturnType<typeof cmmdApiClient.listPullRequests>>,
): PullRequestListResult {
  const projectTitles = new Map(
    result.entries.map((entry) => [entry.projectId, entry.projectTitle]),
  );
  return {
    ...result,
    entries: result.entries.map((entry) => {
      const { checksState, projectId, reviewDecision, ...rest } = entry;
      return {
        ...rest,
        projectId: forgeProjectId(projectId),
        ...(reviewDecision === null || reviewDecision === undefined ? {} : { reviewDecision }),
        ...(checksState === null || checksState === undefined ? {} : { checksState }),
      };
    }),
    errors: result.errors.map((error): PullRequestListProjectError => ({
      projectId: forgeProjectId(error.projectId),
      projectTitle: projectTitles.get(error.projectId) ?? `Project ${error.projectId}`,
      message: error.message,
    })),
  };
}

function normalizeStatsResult(
  result: Awaited<ReturnType<typeof cmmdApiClient.getPullRequestListStats>>,
  refs: ReadonlyArray<PullRequestRef>,
): PullRequestListStatsResult {
  const repositories = new Map(
    refs.flatMap((reference) =>
      reference.projectRepositoryId === undefined
        ? []
        : [
            [
              `${commandProjectId(reference.projectId)}:${reference.projectRepositoryId}:${reference.number}`,
              reference.repository,
            ] as const,
          ],
    ),
  );
  return {
    stats: result.stats.flatMap((stat) => {
      const repository = repositories.get(
        `${stat.projectId}:${stat.projectRepositoryId}:${stat.number}`,
      );
      return repository === undefined
        ? []
        : [
            {
              projectId: forgeProjectId(stat.projectId),
              repository,
              number: stat.number,
              additions: stat.additions,
              deletions: stat.deletions,
            },
          ];
    }),
  };
}

function normalizePullRequestActivity(
  result: Awaited<ReturnType<typeof cmmdApiClient.getPullRequestActivity>>,
): PullRequestActivity {
  return { ...result };
}

function normalizePullRequestDetail(
  result: Awaited<ReturnType<typeof cmmdApiClient.getPullRequestDetail>>,
): PullRequestDetail {
  return {
    ...result,
    projectId: forgeProjectId(result.projectId),
  };
}

function mergePullRequestListPages(
  accumulated: PullRequestListResult,
  continuation: PullRequestListResult,
): PullRequestListResult {
  const entries = new Map(
    accumulated.entries.map((entry) => [
      `${entry.host}:${entry.repository}#${entry.number}`,
      entry,
    ]),
  );
  for (const entry of continuation.entries) {
    entries.set(`${entry.host}:${entry.repository}#${entry.number}`, entry);
  }
  return {
    ...continuation,
    viewers: { ...accumulated.viewers, ...continuation.viewers },
    providers: continuation.providers.length === 0 ? accumulated.providers : continuation.providers,
    entries: [...entries.values()].toSorted((left, right) =>
      right.updatedAt.localeCompare(left.updatedAt),
    ),
    errors: [...accumulated.errors, ...continuation.errors].filter(
      (error, index, errors) =>
        errors.findIndex((candidate) => candidate.projectId === error.projectId) === index,
    ),
  };
}

export function collapsePullRequestListPages(
  data: InfiniteData<PullRequestListResult, PullRequestListPageParam>,
): InfiniteData<PullRequestListResult, PullRequestListPageParam> {
  let latestRootIndex = 0;
  for (const [index, pageParam] of data.pageParams.entries()) {
    if (pageParam.cursors === undefined) latestRootIndex = index;
  }
  const pages = data.pages.slice(latestRootIndex);
  const pageParams = data.pageParams.slice(latestRootIndex);
  const merged = pages.slice(1).reduce(mergePullRequestListPages, pages[0]!);
  return { pages: [merged], pageParams: [pageParams.at(-1)!] };
}

export function pullRequestListInfiniteQueryOptions(input: {
  readonly environmentId: EnvironmentId;
  readonly input: PullRequestListInput;
}) {
  return infiniteQueryOptions({
    queryKey: pullRequestQueryKeys.list(input.environmentId, input.input),
    initialPageParam: {
      limit: input.input.limit ?? DEFAULT_LIST_PAGE_SIZE,
      ...(input.input.cursors === undefined ? {} : { cursors: input.input.cursors }),
    } as PullRequestListPageParam,
    queryFn: ({ pageParam }: { readonly pageParam: PullRequestListPageParam }) =>
      sharedRead(
        [
          ...pullRequestQueryKeys.list(input.environmentId, input.input),
          pageParam.limit,
          pageParam.cursors,
        ],
        async () => {
          const result = await cmmdApiClient.listPullRequests({
            ...commandListInput(input.input),
            limit: pageParam.limit,
            ...(pageParam.cursors === undefined ? {} : { cursors: pageParam.cursors }),
          });
          return normalizeListResult(result);
        },
      ),
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      // A mixed answer can contain cursors for some repositories while another truncated
      // provider cannot issue one. The protocol cannot identify which uncursored repository is
      // incomplete, so grow and replace the root snapshot before following any cursors. This is
      // conservative but lossless; cursor-only continuation is safe once growth reaches its cap.
      if (lastPage.truncated && lastPageParam.limit < MAX_LIST_PAGE_SIZE) {
        return {
          limit: Math.min(lastPageParam.limit + DEFAULT_LIST_PAGE_SIZE, MAX_LIST_PAGE_SIZE),
        };
      }
      return Object.keys(lastPage.nextCursors).length === 0
        ? undefined
        : {
            limit: lastPageParam.limit,
            cursors: lastPage.nextCursors,
          };
    },
    select: collapsePullRequestListPages,
    staleTime: LIST_STALE_TIME_MS,
    gcTime: 5 * 60_000,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });
}

export function pullRequestStatsQueryOptions(input: {
  readonly environmentId: EnvironmentId;
  readonly refs: ReadonlyArray<PullRequestRef>;
}) {
  return queryOptions({
    queryKey: pullRequestQueryKeys.stats(input.environmentId, input.refs),
    queryFn: () =>
      sharedRead(pullRequestQueryKeys.stats(input.environmentId, input.refs), async () =>
        normalizeStatsResult(
          await cmmdApiClient.getPullRequestListStats({
            refs: input.refs.map(commandPullRequestRef),
          }),
          input.refs,
        ),
      ),
    staleTime: STATS_STALE_TIME_MS,
  });
}

export function pullRequestActivityQueryOptions(input: {
  readonly environmentId: EnvironmentId;
  readonly reference: PullRequestRef;
}) {
  return queryOptions({
    queryKey: pullRequestQueryKeys.activity(input.environmentId, input.reference),
    queryFn: () =>
      sharedRead(pullRequestQueryKeys.activity(input.environmentId, input.reference), async () =>
        normalizePullRequestActivity(
          await cmmdApiClient.getPullRequestActivity(commandPullRequestRef(input.reference)),
        ),
      ),
    staleTime: DETAIL_STALE_TIME_MS,
  });
}

export function pullRequestDiffInfiniteQueryOptions(input: {
  readonly environmentId: EnvironmentId;
  readonly input: Omit<PullRequestDiffInput, "cursor">;
}) {
  return infiniteQueryOptions({
    queryKey: pullRequestQueryKeys.diff(input.environmentId, input.input),
    initialPageParam: { cursor: null } as PullRequestDiffPageParam,
    queryFn: ({ pageParam }: { readonly pageParam: PullRequestDiffPageParam }) =>
      cmmdApiClient.getPullRequestDiff({
        ...commandPullRequestRef(input.input),
        ...(input.input.commit === undefined ? {} : { commit: input.input.commit }),
        ...(pageParam.cursor === null ? {} : { cursor: pageParam.cursor }),
      }),
    getNextPageParam: (lastPage: PullRequestDiffResult) =>
      lastPage.nextCursor === null ? undefined : { cursor: lastPage.nextCursor },
    staleTime: DETAIL_STALE_TIME_MS,
    gcTime: 5 * 60_000,
    refetchOnReconnect: true,
  });
}

export function pullRequestDetailQueryOptions(input: {
  readonly environmentId: EnvironmentId;
  readonly reference: PullRequestRef;
}) {
  return queryOptions({
    queryKey: pullRequestQueryKeys.detail(input.environmentId, input.reference),
    queryFn: () =>
      sharedRead(pullRequestQueryKeys.detail(input.environmentId, input.reference), async () =>
        normalizePullRequestDetail(
          await cmmdApiClient.getPullRequestDetail(commandPullRequestRef(input.reference)),
        ),
      ),
    staleTime: DETAIL_STALE_TIME_MS,
  });
}

export function pullRequestThreadCommentsQueryOptions(input: {
  readonly environmentId: EnvironmentId;
  readonly reference: PullRequestRef;
  readonly threadId: string;
  readonly cursor: string;
}) {
  const request = { ...input.reference, threadId: input.threadId, cursor: input.cursor };
  return queryOptions({
    queryKey: pullRequestQueryKeys.threadComments(input.environmentId, request),
    queryFn: () =>
      sharedRead(pullRequestQueryKeys.threadComments(input.environmentId, request), () =>
        cmmdApiClient.getPullRequestThreadComments({
          ...commandPullRequestRef(input.reference),
          threadId: input.threadId,
          cursor: input.cursor,
        }),
      ),
    staleTime: DETAIL_STALE_TIME_MS,
  });
}

export function pullRequestDiffFileContentsQueryOptions(input: {
  readonly environmentId: EnvironmentId;
  readonly input: PullRequestDiffFileContentsInput;
}) {
  return queryOptions({
    queryKey: pullRequestQueryKeys.diffFileContents(input.environmentId, input.input),
    queryFn: () =>
      sharedRead(pullRequestQueryKeys.diffFileContents(input.environmentId, input.input), () =>
        cmmdApiClient.getPullRequestDiffFileContents({
          ...commandPullRequestRef(input.input),
          oldPath: input.input.oldPath,
          newPath: input.input.newPath,
          changeType: input.input.changeType,
          ...(input.input.commit === undefined ? {} : { commit: input.input.commit }),
        }),
      ),
    staleTime: DEFERRED_STALE_TIME_MS,
  });
}

export function pullRequestReviewerCandidatesQueryOptions(input: {
  readonly environmentId: EnvironmentId;
  readonly reference: PullRequestRef;
}) {
  return queryOptions({
    queryKey: pullRequestQueryKeys.reviewerCandidates(input.environmentId, input.reference),
    queryFn: () =>
      sharedRead(
        pullRequestQueryKeys.reviewerCandidates(input.environmentId, input.reference),
        () =>
          cmmdApiClient.getPullRequestReviewerCandidates(commandPullRequestRef(input.reference)),
      ),
    staleTime: DEFERRED_STALE_TIME_MS,
  });
}

export function pullRequestCommandMutationOptions(input: {
  readonly environmentId: EnvironmentId;
  readonly reference?: PullRequestRef;
  readonly queryClient: QueryClient;
  readonly command: () => Promise<void>;
}) {
  return mutationOptions({
    mutationKey: pullRequestMutationKeys.command(),
    scope: { id: `pullRequests:${COMMAND_AUTHORITY_SCOPE}` },
    mutationFn: () => requestState.enqueueCommand(COMMAND_AUTHORITY_SCOPE, input.command),
    onSuccess: async () => {
      await input.queryClient.invalidateQueries({
        queryKey: ["pullRequests"],
      });
    },
  });
}

export interface EnvironmentPullRequestResult<Entry> {
  readonly environmentId: EnvironmentId;
  readonly data: { readonly entries: ReadonlyArray<Entry> } | undefined;
  readonly error: Error | null;
  readonly isPending: boolean;
}

export function mergePullRequestEnvironmentResults<Entry>(
  results: ReadonlyArray<EnvironmentPullRequestResult<Entry & object>>,
) {
  return {
    entries: results.flatMap((result) =>
      (result.data?.entries ?? []).map((entry) =>
        Object.assign({}, entry, { environmentId: result.environmentId }),
      ),
    ),
    error: results.find((result) => result.error !== null)?.error ?? null,
    isPending: results.some((result) => result.isPending),
    environments: results,
  };
}

export function pullRequestListCursorByEnvironment(
  results: ReadonlyArray<{
    readonly environmentId: EnvironmentId;
    readonly nextCursors: PullRequestListCursors;
  }>,
): Readonly<Record<string, PullRequestListCursors>> {
  return Object.fromEntries(
    results.flatMap((result) =>
      Object.keys(result.nextCursors).length === 0
        ? []
        : [[result.environmentId, result.nextCursors] as const],
    ),
  );
}

export interface ScopedPullRequestRow<Entry> {
  readonly environmentId: EnvironmentId;
  readonly involvement: "authored" | "reviewing" | "other";
  readonly entry: Entry & PullRequestRef;
}

export function pullRequestRowKey(
  row: ScopedPullRequestRow<PullRequestRef & { readonly host?: string }>,
): string {
  return `${row.environmentId}:${row.entry.host ?? ""}:${row.entry.repository}#${row.entry.number}`;
}

export function partitionPullRequestsByPriority<Entry extends PullRequestRef>(
  rows: ReadonlyArray<ScopedPullRequestRow<Entry>>,
): ReadonlyArray<ScopedPullRequestRow<Entry>> {
  const byKey = new Map<string, ScopedPullRequestRow<Entry>>();
  for (const row of rows) {
    const key = pullRequestRowKey(row);
    const held = byKey.get(key);
    if (held === undefined || row.involvement === "authored") byKey.set(key, row);
  }
  const priority = { authored: 0, reviewing: 1, other: 2 } as const;
  return [...byKey.values()].toSorted(
    (left, right) => priority[left.involvement] - priority[right.involvement],
  );
}
